﻿import { useEffect, useState } from 'react';
import {
  Alert,
  ButtonLink,
  Card,
  CommandBlock,
  Eyebrow,
  Heading,
  Lead,
  Section,
  cx,
} from '../components/ui';
import { usePageTitle } from '../lib/usePageTitle';

type Platform = 'windows' | 'macos' | 'linux';

/** The signed manifest the build publishes alongside the artefacts. */
const RELEASE_MANIFEST = '/releases/release-manifest.json';

interface PlatformDownload {
  id: Platform;
  name: string;
  detail: string;
  artifact: string;
  install: string;
  verify: string;
}

/**
 * Per-platform download and verification instructions for one release.
 *
 * Built from the version in the release manifest rather than a constant in this
 * file. A hardcoded version here is not a small duplication: it is a second
 * source of truth that no build step updates, so every release silently leaves
 * the download page advertising files that are no longer the current ones - and
 * the filenames are derived from it, so the buttons 404 rather than degrade.
 *
 * The verify commands sit next to the download rather than in documentation.
 * A checksum nobody runs protects nobody.
 */
function downloadsFor(version: string): PlatformDownload[] {
  return [
    {
      id: 'windows',
      name: 'Windows',
      detail: 'Windows 10 or later, 64-bit · Python 3.11+',
      artifact: `waveqa-agent-${version}-windows.zip`,
      install: `Expand-Archive waveqa-agent-${version}-windows.zip
cd waveqa-agent-${version}-windows
.\\install.ps1
waveqa setup`,
      verify: `# Compare against the published SHA256SUMS before running anything
Get-FileHash waveqa-agent-${version}-windows.zip -Algorithm SHA256`,
    },
    {
      id: 'macos',
      name: 'macOS',
      detail: 'macOS 13 or later, Apple silicon or Intel · Python 3.11+',
      artifact: `waveqa-agent-${version}-macos.tar.gz`,
      install: `tar -xzf waveqa-agent-${version}-macos.tar.gz
cd waveqa-agent-${version}-macos
./install.sh
waveqa setup`,
      verify: `# Compare against the published SHA256SUMS before running anything
shasum -a 256 waveqa-agent-${version}-macos.tar.gz`,
    },
    {
      id: 'linux',
      name: 'Linux',
      detail: 'glibc 2.31+ (Ubuntu 20.04, Debian 11, RHEL 9) · Python 3.11+',
      artifact: `waveqa-agent-${version}-linux.tar.gz`,
      install: `tar -xzf waveqa-agent-${version}-linux.tar.gz
cd waveqa-agent-${version}-linux
./install.sh
waveqa setup`,
      verify: `# Compare against the published SHA256SUMS before running anything
sha256sum waveqa-agent-${version}-linux.tar.gz`,
    },
  ];
}

type ReleaseState =
  | { status: 'loading' }
  | { status: 'ready'; version: string }
  | { status: 'unavailable' };

/**
 * Read the current release version from the published manifest.
 *
 * Only the version is taken, and only to build filenames and label the panel.
 * This is not a trust boundary and does not pretend to be one: a browser cannot
 * check an Ed25519 signature usefully here, because anything able to tamper
 * with the manifest could serve a page that skips the check. Trust comes from
 * the visitor comparing the checksum, and from the agent verifying the
 * signature against the key it embeds.
 */
function useRelease(): ReleaseState {
  const [state, setState] = useState<ReleaseState>({ status: 'loading' });

  useEffect(() => {
    // Guarded so a response arriving after unmount cannot set state on a dead
    // component, and so a slow response cannot overwrite a newer one.
    let cancelled = false;

    // `no-store`: the manifest changes on every release and is tiny. A cached
    // copy here reintroduces exactly the staleness this hook exists to remove.
    fetch(RELEASE_MANIFEST, { cache: 'no-store' })
      .then((response) => {
        if (!response.ok) throw new Error(`manifest returned ${response.status}`);
        return response.json() as Promise<unknown>;
      })
      .then((manifest) => {
        if (cancelled) return;
        const version = (manifest as { version?: unknown }).version;
        // Validated rather than trusted: this string is interpolated into
        // download URLs, and the shape is known exactly.
        //
        // The pre-release suffix is part of that shape. This pattern used to be
        // `\d+\.\d+\.\d+` and nothing else, which silently hid every download
        // the moment a release was cut as `2.0.0-beta` - the manifest and all
        // three artifacts were published and correct, and the panel reported
        // that no release could be read. A beta is exactly when people are most
        // likely to be sent here to fetch a build.
        //
        // Still safe to interpolate: the suffix admits alphanumerics, dots and
        // hyphens only. No slash, backslash, percent, query or fragment
        // character can appear, so no value matching this can escape the
        // `/releases/` path it is placed into.
        if (
          typeof version === 'string' &&
          /^\d+\.\d+\.\d+(?:-[0-9A-Za-z.-]+)?$/.test(version)
        ) {
          setState({ status: 'ready', version });
        } else {
          setState({ status: 'unavailable' });
        }
      })
      .catch(() => {
        if (!cancelled) setState({ status: 'unavailable' });
      });

    return () => {
      cancelled = true;
    };
  }, []);

  return state;
}

/** Best guess at the visitor's platform, so the right tab opens first. */
function detectPlatform(): Platform {
  if (typeof navigator === 'undefined') return 'windows';

  const hint = `${navigator.userAgent} ${navigator.platform ?? ''}`.toLowerCase();
  if (hint.includes('mac')) return 'macos';
  if (hint.includes('linux') || hint.includes('x11')) return 'linux';
  return 'windows';
}

function DownloadPanel() {
  const [selected, setSelected] = useState<Platform>('windows');
  const release = useRelease();

  // Detection runs after mount rather than during render â€” `navigator` does not
  // exist if this is ever server-rendered, and guessing during render would
  // make the first paint differ from the second.
  useEffect(() => setSelected(detectPlatform()), []);

  if (release.status !== 'ready') {
    // No fallback version is rendered. Showing a stale number beside live
    // download buttons is worse than showing none: the buttons would point at
    // filenames that no longer exist, and the visitor would have no way to tell
    // that is what happened.
    return (
      <Card className="mt-8">
        <h3 className="text-base font-semibold text-white">Download WaveQA Agent</h3>
        <p className="mt-3 text-sm text-hull-400">
          {release.status === 'loading'
            ? 'Loading the current releaseâ€¦'
            : 'The release manifest could not be read, so no download is offered here.'}
        </p>
        {release.status === 'unavailable' && (
          <p className="mt-2 text-xs text-hull-500">
            Browse{' '}
            <a href="/releases/" className="text-surge-400 hover:text-surge-300">
              /releases/
            </a>{' '}
            directly, and verify whatever you take against{' '}
            <a
              href="/releases/SHA256SUMS"
              className="text-surge-400 hover:text-surge-300"
            >
              SHA256SUMS
            </a>
            .
          </p>
        )}
      </Card>
    );
  }

  const downloads = downloadsFor(release.version);
  const platform = downloads.find((p) => p.id === selected) ?? downloads[0]!;

  return (
    <Card className="mt-8">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h3 className="text-base font-semibold text-white">Download WaveQA Agent</h3>
        <span className="text-xs text-hull-400">
          Version {release.version} · Python 3.11 or later
        </span>
      </div>

      <div
        role="tablist"
        aria-label="Operating system"
        className="mt-5 flex flex-wrap gap-2"
      >
        {downloads.map((p) => (
          <button
            key={p.id}
            role="tab"
            type="button"
            aria-selected={selected === p.id}
            onClick={() => setSelected(p.id)}
            className={cx(
              'rounded-lg border px-4 py-2 text-sm transition',
              selected === p.id
                ? 'border-surge-500 bg-surge-500/10 font-medium text-white'
                : 'border-hull-600 text-hull-300 hover:border-hull-500 hover:text-white',
            )}
          >
            {p.name}
          </button>
        ))}
      </div>

      <div className="mt-5">
        <p className="text-sm text-hull-400">{platform.detail}</p>

        <div className="mt-4 flex flex-wrap items-center gap-3">
          <ButtonLink to={`/releases/${platform.artifact}`} download>
            Download for {platform.name}
          </ButtonLink>
          <code className="rounded-lg border border-hull-700 bg-hull-950 px-3 py-2 font-mono text-xs text-hull-400">
            {platform.artifact}
          </code>
        </div>

        <div className="mt-4 flex flex-wrap gap-4 text-xs">
          <a href="/releases/SHA256SUMS" className="text-surge-400 hover:text-surge-300">
            SHA256SUMS
          </a>
          <a
            href="/releases/release-manifest.json"
            className="text-surge-400 hover:text-surge-300"
          >
            Signed release manifest
          </a>
          <span className="text-hull-500">Ed25519 · manifest covers every hash</span>
        </div>

        <div className="mt-4">
          <Alert tone="info">
            <strong className="text-white">Verify before you run it.</strong> The
            manifest is signed with the WaveQA release key, which the agent embeds
            â€” so <code className="text-surge-400">waveqa update</code> refuses any
            artefact whose hash does not match, and refuses a manifest that was
            not signed by us. Do the same by hand with the command below.
            <span className="mt-2 block text-hull-400">
              These archives are not yet Authenticode or Apple Developer ID
              signed, so SmartScreen and Gatekeeper will still warn. That needs
              certificates issued against a verified legal identity.
            </span>
          </Alert>
        </div>

        {/*
          `items-stretch` plus `flex-1` on each block makes the two boxes end on
          the same line even though the commands differ in length - otherwise
          the shorter column stops short and the pair looks misaligned.
        */}
        <div className="mt-2 grid items-stretch gap-4 lg:grid-cols-2">
          <div className="flex flex-col">
            <p className="mb-2 text-sm font-medium text-hull-200">
              1. Verify the download
            </p>
            <CommandBlock className="flex-1" label="Copy verification commands">
              {platform.verify}
            </CommandBlock>
          </div>

          <div className="flex flex-col">
            <p className="mb-2 text-sm font-medium text-hull-200">2. Install</p>
            <CommandBlock className="flex-1" label="Copy install commands">
              {platform.install}
            </CommandBlock>
          </div>
        </div>

        <p className="mt-3 text-xs text-hull-400">
          Then run <code className="text-surge-400">waveqa doctor</code> â€” it checks
          credentials, working folder, keychain, LLM configuration, browser
          runtime and live subscription state, and names exactly what is missing.
        </p>
      </div>
    </Card>
  );
}

const STEPS = [
  {
    title: 'Download WaveQA for your machine',
    body: 'WaveQA runs on Windows, Linux and macOS. The agent executes on your own hardware â€” your specifications, evidence and reports never leave it. Pick your platform below.',
    detail: 'Windows · macOS · Linux â€” see the download panel above',
  },
  {
    title: 'Configure it',
    body: 'Run `waveqa setup` â€” or `waveqa setup portal` for a browser UI. You supply your agent credentials, a working folder, and an LLM provider. Secrets go straight to your OS keychain, never a config file.',
    detail: '$ waveqa setup',
  },
  {
    title: 'Create a project',
    body: 'Running `waveqa -p` against a new project creates its folder and stops. The documents are yours to write - the templates are a reference, not a starting file. WaveQA will refuse to verify against an unedited template rather than report on the template itself.',
    detail: '$ waveqa -p "Checkout"',
  },
  {
    title: 'Provide the two documents',
    body: 'The readme sits in the project folder and names where the application runs. The feature spec sits in the feature folder and carries the acceptance criteria for that feature. Those two are the whole input to a run - WaveQA reads nothing else about your product.',
    detail: 'readme.md · Feature-01/feature-spec.md',
  },
  {
    title: 'Run the verification',
    body: 'WaveQA checks entitlement with the server, builds a plan from your documented routes and quoted copy, then executes deterministic checks against the running application.',
    detail: 'Documents 2 written · 3 derived · 12 acceptance criteria',
  },
  {
    title: 'Collect evidence',
    body: 'Screenshots, HTTP exchanges and console output are captured as the run happens, redacted, and written into a timestamped run folder alongside a full execution log.',
    detail: 'evidence/ac-04-01.png',
  },
  {
    title: 'Review the report',
    body: 'Each criterion gets a verdict and the artefact that justifies it. Anything unverifiable is reported as such. Usage is metered on the server and appears in your dashboard.',
    detail: 'acceptance-results.md',
  },
] as const;

export function HowItWorks() {
  usePageTitle('How It Works â€” WaveQA');

  return (
    <>
      <Section className="border-b border-hull-800">
        <Eyebrow>How it works</Eyebrow>
        <Heading level={1} className="text-white">
          Six steps, then a report you can defend
        </Heading>
        <Lead className="mt-5 max-w-2xl">
          WaveQA runs on your machine and talks to the WaveQA API only for
          authorization and metering. Your specifications, evidence and reports
          stay where you put them.
        </Lead>

        <DownloadPanel />
      </Section>

      <Section className="border-b border-hull-800">
        <ol className="space-y-4">
          {STEPS.map((step, index) => (
            <Card as="li" key={step.title} className="sm:flex sm:gap-6">
              <div className="mb-3 flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-surge-600/50 bg-surge-500/10 text-sm font-semibold text-surge-300 sm:mb-0">
                {index + 1}
              </div>
              <div className="min-w-0 flex-1">
                <h2 className="text-base font-semibold text-white">{step.title}</h2>
                <p className="mt-2 text-sm leading-relaxed text-hull-300">{step.body}</p>
                <p className="mt-3 overflow-x-auto rounded-lg border border-hull-700 bg-hull-950 px-3 py-2 font-mono text-xs text-surge-300">
                  {step.detail}
                </p>
              </div>
            </Card>
          ))}
        </ol>
      </Section>

      <Section className="border-b border-hull-800">
        <Heading className="text-white">What leaves your machine</Heading>
        <Lead className="mt-3 max-w-2xl">
          Worth being explicit about, because it is the question every security
          reviewer asks first.
        </Lead>

        <div className="mt-8 grid gap-4 md:grid-cols-2">
          <Card>
            <h3 className="text-base font-semibold text-pass-400">Sent to WaveQA</h3>
            <ul className="mt-3 space-y-2 text-sm text-hull-300">
              {[
                'Your client id, to authenticate',
                'Run identifier, project and feature name',
                'Start and finish timestamps',
                'Counts: how many criteria passed, failed, were blocked',
                'Agent version and configured model name',
              ].map((item) => (
                <li key={item} className="flex gap-2">
                  <span aria-hidden="true" className="text-surge-400">
                    ·
                  </span>
                  {item}
                </li>
              ))}
            </ul>
          </Card>

          <Card>
            <h3 className="text-base font-semibold text-white">Stays on your machine</h3>
            <ul className="mt-3 space-y-2 text-sm text-hull-300">
              {[
                'Your specifications and architecture documents',
                'Criterion text and report contents',
                'Screenshots and every other evidence artefact',
                'Application URLs and response bodies',
                'Your LLM API key and client secret',
              ].map((item) => (
                <li key={item} className="flex gap-2">
                  <span aria-hidden="true" className="text-surge-400">
                    ·
                  </span>
                  {item}
                </li>
              ))}
            </ul>
            <p className="mt-4 border-t border-hull-700 pt-4 text-xs text-hull-400">
              Project content is sent to your configured LLM provider only, and
              only because you configured it. WaveQA never receives it.
            </p>
          </Card>
        </div>
      </Section>

      <Section>
        <Card className="text-center">
          <Heading className="text-white">Ready to try it?</Heading>
          <div className="mt-7 flex flex-wrap justify-center gap-3">
            <ButtonLink to="/register">Get Started</ButtonLink>
            <ButtonLink to="/docs" variant="secondary">
              Read the docs
            </ButtonLink>
          </div>
        </Card>
      </Section>
    </>
  );
}
